/**
 * InsightDashboard Component
 *
 * A comprehensive insight view inspired by Vanguard's interface.
 * Shows product type breakdown, top investments, sector and region allocation.
 */

import React, { useMemo, useState } from 'react';
import { TrendingUp, PieChart, BarChart3, Info, Globe, AlertCircle } from 'lucide-react';
import { useCountry } from '../../CountryContext';
import { getCurrencyCode, getCurrencySymbol, formatCurrency as formatCurrencyConfig } from '../../countryConfig';
import {
  SECTORS,
  REGIONS,
  SECTOR_COLORS,
  REGION_COLORS,
  calculatePortfolioSectorAllocation,
  calculatePortfolioRegionAllocation
} from '../../utils/sectorData';
import SectorBreakdownModal from './SectorBreakdownModal';

const InsightDashboard = ({
  investments = [],
  totalValue = 0,
  totalContributions = 0,
  isDark = false,
  wealthSnapshots = [],
  onEditInvestmentAllocation // Callback to open sector/region editor for an investment
}) => {
  const { selectedCountry } = useCountry();
  const currencySymbol = getCurrencySymbol(selectedCountry);

  // State for sector breakdown modal
  const [showSectorModal, setShowSectorModal] = useState(false);

  // Calculate product type allocation (Equity vs Cash vs Bonds)
  const productTypes = useMemo(() => {
    const types = {
      'Equity': { value: 0, color: '#f97316' },
      'Bonds': { value: 0, color: '#8b5cf6' },
      'Cash': { value: 0, color: '#22c55e' },
      'Other': { value: 0, color: '#6b7280' }
    };

    investments.forEach(inv => {
      const value = inv.amount || (inv.quantity * (inv.currentPrice || inv.pricePerUnit || 0));
      const category = inv.category || 'Other';

      if (['Stocks', 'ETF'].includes(category)) {
        types['Equity'].value += value;
      } else if (category === 'Bonds') {
        types['Bonds'].value += value;
      } else if (category === 'Cash') {
        types['Cash'].value += value;
      } else {
        types['Other'].value += value;
      }
    });

    const total = Object.values(types).reduce((sum, t) => sum + t.value, 0);

    return Object.entries(types)
      .filter(([_, data]) => data.value > 0)
      .map(([name, data]) => ({
        name,
        value: data.value,
        percentage: total > 0 ? ((data.value / total) * 100).toFixed(0) : 0,
        color: data.color
      }));
  }, [investments]);

  // Top 10 investments
  const topInvestments = useMemo(() => {
    const sorted = [...investments]
      .map(inv => ({
        ...inv,
        currentValue: inv.amount || (inv.quantity * (inv.currentPrice || inv.pricePerUnit || 0))
      }))
      .sort((a, b) => b.currentValue - a.currentValue)
      .slice(0, 10);

    const total = sorted.reduce((sum, inv) => sum + inv.currentValue, 0);

    return sorted.map((inv, idx) => ({
      name: inv.name,
      ticker: inv.ticker,
      value: inv.currentValue,
      percentage: total > 0 ? ((inv.currentValue / total) * 100) : 0,
      color: getBarColor(idx)
    }));
  }, [investments]);

  // Sector allocation - from manually entered data only
  const sectorAllocation = useMemo(() => {
    const portfolioSectors = calculatePortfolioSectorAllocation(investments);

    // Convert to display format with colors
    const sectors = SECTORS
      .map(sector => ({
        name: sector,
        value: portfolioSectors.totals[sector] || 0,
        percentage: portfolioSectors.percentages[sector] || 0,
        color: SECTOR_COLORS[sector] || '#6b7280'
      }))
      .filter(sector => sector.percentage > 0.5) // Only show sectors with > 0.5%
      .sort((a, b) => b.percentage - a.percentage);

    return {
      sectors,
      coveragePercent: portfolioSectors.coveragePercent,
      hasData: portfolioSectors.coveragePercent > 0
    };
  }, [investments]);

  // Region allocation - from manually entered data only
  const regionAllocation = useMemo(() => {
    const portfolioRegions = calculatePortfolioRegionAllocation(investments);

    // Convert to display format with colors
    const regions = REGIONS
      .map(region => ({
        name: region,
        value: portfolioRegions.totals[region] || 0,
        percentage: portfolioRegions.percentages[region] || 0,
        color: REGION_COLORS[region] || '#6b7280'
      }))
      .filter(region => region.percentage > 0.5) // Only show regions with > 0.5%
      .sort((a, b) => b.percentage - a.percentage);

    return {
      regions,
      coveragePercent: portfolioRegions.coveragePercent,
      hasData: portfolioRegions.coveragePercent > 0
    };
  }, [investments]);

  // Performance over last 3 months (from snapshots)
  const threeMonthPerformance = useMemo(() => {
    if (!wealthSnapshots || wealthSnapshots.length === 0) {
      return {
        startedWith: 0,
        contributed: totalContributions,
        returns: totalValue - totalContributions,
        endedWith: totalValue,
        rateOfReturn: totalContributions > 0 ? ((totalValue - totalContributions) / totalContributions) * 100 : 0,
        hasData: false
      };
    }

    const sorted = [...wealthSnapshots].sort((a, b) => b.month.localeCompare(a.month));
    const threeMonthSnapshots = sorted.slice(0, 3);
    const threeMonthsAgoSnapshot = sorted[Math.min(3, sorted.length - 1)];

    const startedWith = threeMonthsAgoSnapshot?.netWorth || threeMonthsAgoSnapshot?.totalAssets || 0;
    const endedWith = totalValue;

    let contributed = 0;
    let withdrawals = 0;
    threeMonthSnapshots.forEach(snapshot => {
      contributed += snapshot.changes?.newContributions || 0;
      withdrawals += snapshot.changes?.withdrawals || 0;
    });
    const netContributed = contributed - withdrawals;

    const returns = endedWith - startedWith - netContributed;
    const avgInvestment = startedWith + (netContributed / 2);
    const rateOfReturn = avgInvestment > 0
      ? (returns / avgInvestment) * 100
      : (netContributed > 0 ? (returns / netContributed) * 100 : 0);

    return {
      startedWith,
      contributed: netContributed,
      returns,
      endedWith,
      rateOfReturn,
      hasData: true
    };
  }, [wealthSnapshots, totalValue, totalContributions]);

  const formatValue = (value) => {
    return formatCurrencyConfig(value, selectedCountry);
  };

  function getBarColor(index) {
    const colors = [
      '#3b82f6', '#2563eb', '#60a5fa', '#1d4ed8',
      '#3b82f6', '#2563eb', '#60a5fa', '#1d4ed8',
      '#93c5fd', '#bfdbfe'
    ];
    return colors[index % colors.length];
  }

  // No data guidance component
  const NoDataGuidance = ({ type }) => (
    <div className={`text-center py-6 px-4 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
      <AlertCircle className={`w-8 h-8 mx-auto mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
      <p className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
        No {type} data available
      </p>
      <p className={`text-xs mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
        Add {type} allocation data to your investments from their fund factsheets.
      </p>
      <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
        <p className="font-medium mb-1">Where to find this:</p>
        <p>Fund Factsheet → Portfolio Breakdown section</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Product Type Bar */}
      <div className={`rounded-xl border p-6 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Product type
        </h3>
        {/* Stacked bar visualization */}
        <div className="h-8 flex rounded-lg overflow-hidden mb-4">
          {productTypes.map((type, idx) => (
            <div
              key={type.name}
              style={{ width: `${type.percentage}%`, backgroundColor: type.color }}
              className="h-full transition-all hover:opacity-80"
              title={`${type.name}: ${type.percentage}%`}
            />
          ))}
        </div>
        {/* Legend */}
        <div className="flex flex-wrap gap-6">
          {productTypes.map(type => (
            <div key={type.name} className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: type.color }} />
              <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {type.name} ({type.percentage}%)
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Top 10 Investments */}
      <div className={`rounded-xl border p-6 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Top 10 investments
        </h3>
        <div className="flex flex-wrap items-end gap-3">
          {topInvestments.map((inv, idx) => (
            <div key={inv.name} className="text-center min-w-[70px]">
              <div
                className="w-14 mx-auto rounded-t"
                style={{
                  height: `${Math.max(30, inv.percentage * 3)}px`,
                  backgroundColor: inv.color
                }}
              />
              <p className={`text-sm font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {inv.percentage.toFixed(1)}%
              </p>
              <p className={`text-xs truncate max-w-[80px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`} title={inv.name}>
                {inv.ticker || inv.name.split(' ')[0]}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Region Allocation */}
      <div className={`rounded-xl border p-6 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Globe className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Region allocation
            </h3>
          </div>
          {regionAllocation.hasData && (
            <span className={`text-xs px-2 py-1 rounded-full ${isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'}`}>
              {regionAllocation.coveragePercent.toFixed(0)}% of portfolio covered
            </span>
          )}
        </div>

        {regionAllocation.hasData ? (
          <div className="flex flex-wrap items-end gap-3">
            {regionAllocation.regions.map((region, idx) => (
              <div key={region.name} className="text-center min-w-[70px]">
                <div
                  className="w-14 mx-auto rounded-t"
                  style={{
                    height: `${Math.max(30, region.percentage * 2)}px`,
                    backgroundColor: region.color
                  }}
                />
                <p className={`text-sm font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {region.percentage.toFixed(0)}%
                </p>
                <p className={`text-xs truncate max-w-[80px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`} title={region.name}>
                  {region.name.split(' ')[0]}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <NoDataGuidance type="region" />
        )}
      </div>

      {/* Sector Allocation */}
      <div className={`rounded-xl border p-6 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <PieChart className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Sector allocation
            </h3>
          </div>
          <div className="flex items-center gap-3">
            {sectorAllocation.hasData && (
              <span className={`text-xs px-2 py-1 rounded-full ${isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'}`}>
                {sectorAllocation.coveragePercent.toFixed(0)}% of portfolio covered
              </span>
            )}
            {sectorAllocation.hasData && (
              <button
                onClick={() => setShowSectorModal(true)}
                className={`text-sm ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'} hover:underline`}
              >
                More
              </button>
            )}
          </div>
        </div>

        {sectorAllocation.hasData ? (
          <div className="flex flex-wrap items-end gap-3">
            {sectorAllocation.sectors.map((sector, idx) => (
              <div key={sector.name} className="text-center min-w-[70px]">
                <div
                  className="w-14 mx-auto rounded-t"
                  style={{
                    height: `${Math.max(30, sector.percentage * 3)}px`,
                    backgroundColor: sector.color
                  }}
                />
                <p className={`text-sm font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {sector.percentage.toFixed(1)}%
                </p>
                <p className={`text-xs truncate max-w-[80px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`} title={sector.name}>
                  {sector.name.split(' ').slice(0, 2).join(' ')}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <NoDataGuidance type="sector" />
        )}
      </div>

      {/* 3-Month Performance */}
      <div className={`rounded-xl border p-6 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Your performance over the last three months
        </h3>
        <div className="flex flex-col lg:flex-row items-center justify-center gap-8">
          {/* Left side - Starting values */}
          <div className="text-center lg:text-right space-y-2">
            <div>
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>You started with</span>
              <p className="text-xl font-bold text-green-600">{formatValue(threeMonthPerformance.startedWith)}</p>
            </div>
            <div>
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>You contributed and withdrew</span>
              <p className="text-xl font-bold text-green-600">{formatValue(threeMonthPerformance.contributed)}</p>
            </div>
            <div>
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Your investments returned to You</span>
              <p className={`text-xl font-bold ${threeMonthPerformance.returns >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatValue(threeMonthPerformance.returns)}
              </p>
            </div>
          </div>

          {/* Center bracket */}
          <div className="text-4xl text-gray-300">{'}'}</div>

          {/* Right side */}
          <div className="text-center lg:text-left">
            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>You ended up with</span>
            <p className="text-3xl font-bold text-green-600">{formatValue(threeMonthPerformance.endedWith)}</p>
          </div>

          {/* Rate of Return */}
          <div className="text-center lg:text-left">
            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Your rate of return was</span>
            <p className={`text-2xl font-bold ${threeMonthPerformance.rateOfReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {threeMonthPerformance.rateOfReturn.toFixed(2)}%
            </p>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className={`p-4 rounded-lg ${isDark ? 'bg-yellow-900/20 border border-yellow-500/30' : 'bg-yellow-50 border border-yellow-200'}`}>
        <p className={`text-sm ${isDark ? 'text-yellow-300' : 'text-yellow-700'}`}>
          Please note Insight totals may not always equal 100% due to rounding.
          Sector and region data is based on information you enter from your fund factsheets.
        </p>
      </div>

      {/* Sector Breakdown Modal */}
      <SectorBreakdownModal
        isOpen={showSectorModal}
        onClose={() => setShowSectorModal(false)}
        investments={investments}
        isDark={isDark}
      />
    </div>
  );
};

export default InsightDashboard;
